/**
 * 画像クロッパー モジュール (Canvas API実装)
 * note/Instagram風のUIで画像をクロップする機能を提供
 * 選択枠は中央固定、画像をドラッグして調整
 */
class ImageCropper {
    constructor() {
        this.modal = null;
        this.container = null;
        this.zoomSlider = null;
        this.currentFile = null;
        this.callback = null;
        this.aspectRatio = 16 / 9;
        this.circularCrop = false;
        this.initialized = false;

        // Canvas要素
        this.canvas = null;
        this.ctx = null;

        // 画像
        this.image = null;
        this.imageLoaded = false;

        // ズーム管理（初期は最小=1.0、そこから拡大のみ）
        this.currentZoom = 1.0;
        this.minZoom = 1.0;
        this.maxZoom = 3.0;

        // 画像の表示情報
        this.imageInfo = {
            naturalWidth: 0,
            naturalHeight: 0,
            baseScale: 1
        };

        // 画像の現在位置（ドラッグで変更）
        this.imagePosition = {
            x: 0,
            y: 0
        };

        // 選択枠の情報（中央固定）
        this.selection = {
            x: 0,
            y: 0,
            width: 0,
            height: 0
        };

        // ドラッグ状態
        this.isDragging = false;
        this.dragStart = { x: 0, y: 0 };
        this.imagePositionStart = { x: 0, y: 0 };

        // ピンチズーム状態
        this.isPinching = false;
        this.pinchStartDistance = 0;
        this.pinchStartZoom = 1.0;
        this.pinchCenter = { x: 0, y: 0 };

        // バインドされたイベントハンドラ
        this.boundMouseDown = this.handleMouseDown.bind(this);
        this.boundMouseMove = this.handleMouseMove.bind(this);
        this.boundMouseUp = this.handleMouseUp.bind(this);
        this.boundTouchStart = this.handleTouchStart.bind(this);
        this.boundTouchMove = this.handleTouchMove.bind(this);
        this.boundTouchEnd = this.handleTouchEnd.bind(this);
    }

    /**
     * モーダルを初期化
     * モーダルが存在しない場合は静かにスキップ（すべてのページで読み込まれるため）
     */
    init(modalId = 'imageCropperModal') {
        this.modal = document.getElementById(modalId);
        if (!this.modal) {
            // モーダルが存在しないページでは何もしない（エラーを出さない）
            return;
        }
        this.container = document.getElementById('cropperContainer');
        this.zoomSlider = document.getElementById('zoomSlider');
        this.initialized = true;
    }

    /**
     * クロッパーを開く
     */
    open(file, callback, aspectRatio = null, circularCrop = false) {
        // 常にDOMから再取得（wire:navigate等でDOM差し替えに対応）
        this.init();

        if (!this.modal || !this.container) {
            console.error('Cropper not properly initialized: modal or container not found in DOM');
            return;
        }

        // ファイルサイズ事前チェック（ブラウザのメモリ枯渇防止）
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
        if (file.size > MAX_FILE_SIZE) {
            alert('画像サイズが大きすぎます。10MB以下のファイルを選択してください。');
            return;
        }

        this.currentFile = file;
        this.callback = callback;
        this.currentZoom = 1.0;
        this.circularCrop = circularCrop;

        if (aspectRatio !== null) {
            this.aspectRatio = aspectRatio;
        }

        this.showModal();
        this.loadImageFromFile(file);
    }

    /**
     * モーダルを表示
     */
    showModal() {
        this.modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        // スライダーを0（最小ズーム）に
        if (this.zoomSlider) {
            this.zoomSlider.value = 0;
        }
    }

    /**
     * File から画像を読み込み
     * モダンブラウザ (Chrome 79+/Firefox 91+/Safari 15.4+) は EXIF Orientation を
     * 自動で honor して回転済みの ImageBitmap を返すため、自前回転は不要。
     */
    async loadImageFromFile(file) {
        try {
            const bitmap = await createImageBitmap(file);
            this.image = bitmap;
            this.imageLoaded = true;
            // モーダルのレイアウト完了を待ってからCanvas初期化
            requestAnimationFrame(() => {
                this.setupCanvas();
                this.calculateLayout();
                this.draw();
                this.addEventListeners();
            });
        } catch (err) {
            console.error('画像読み込みエラー', err);
            this.close();
            alert('画像を読み込めませんでした。HEIC など未対応の形式の可能性があります。jpg / png / gif の画像を選んでください。');
        }
    }

    /**
     * Canvasを作成・設定
     */
    setupCanvas() {
        this.container.innerHTML = '';

        this.canvas = document.createElement('canvas');
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.style.cursor = 'grab';

        this.container.appendChild(this.canvas);

        // 高解像度対応
        const rect = this.container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = rect.width * dpr;
        this.canvas.height = rect.height * dpr;

        this.ctx = this.canvas.getContext('2d');
        this.ctx.scale(dpr, dpr);

        // 論理サイズを保存
        this.canvasWidth = rect.width;
        this.canvasHeight = rect.height;
    }

    /**
     * レイアウトを計算
     */
    calculateLayout() {
        const img = this.image;
        const containerWidth = this.canvasWidth;
        const containerHeight = this.canvasHeight;

        // 選択枠のサイズを計算（コンテナの80%で、アスペクト比を維持）
        let selWidth, selHeight;
        const containerAspect = containerWidth / containerHeight;

        if (this.aspectRatio > containerAspect) {
            // 横長の選択枠
            selWidth = containerWidth * 0.8;
            selHeight = selWidth / this.aspectRatio;
        } else {
            // 縦長の選択枠
            selHeight = containerHeight * 0.8;
            selWidth = selHeight * this.aspectRatio;
        }

        // 選択枠を中央に配置（固定）
        this.selection = {
            x: (containerWidth - selWidth) / 2,
            y: (containerHeight - selHeight) / 2,
            width: selWidth,
            height: selHeight
        };

        // ImageBitmap は naturalWidth を持たず width/height のため両対応
        const naturalWidth = img.naturalWidth || img.width;
        const naturalHeight = img.naturalHeight || img.height;

        // 画像全体が選択枠内に収まるスケールを計算（余白が出る contain 方式）
        const scaleX = selWidth / naturalWidth;
        const scaleY = selHeight / naturalHeight;
        const baseScale = Math.min(scaleX, scaleY);

        this.imageInfo = {
            naturalWidth: naturalWidth,
            naturalHeight: naturalHeight,
            baseScale: baseScale
        };

        // 画像を中央に配置
        this.centerImage();
    }

    /**
     * 画像を中央に配置
     */
    centerImage() {
        const scale = this.imageInfo.baseScale * this.currentZoom;
        const displayWidth = this.imageInfo.naturalWidth * scale;
        const displayHeight = this.imageInfo.naturalHeight * scale;

        this.imagePosition = {
            x: (this.canvasWidth - displayWidth) / 2,
            y: (this.canvasHeight - displayHeight) / 2
        };
    }

    /**
     * 現在の画像サイズを取得
     */
    getImageSize() {
        const scale = this.imageInfo.baseScale * this.currentZoom;
        return {
            width: this.imageInfo.naturalWidth * scale,
            height: this.imageInfo.naturalHeight * scale
        };
    }

    /**
     * 画像位置を制限
     * - 画像が選択枠より大きい軸: 選択枠が画像内に収まる（cover の制約）
     * - 画像が選択枠より小さい軸: 画像が選択枠内に収まる（contain の制約）
     * X / Y を独立判定するため、片軸 cover・片軸 contain も滑らかに扱える
     */
    constrainImagePosition() {
        const imgSize = this.getImageSize();
        const sel = this.selection;
        const pos = this.imagePosition;

        if (imgSize.width >= sel.width) {
            // cover: 画像の左端 ≤ 選択枠左端、画像の右端 ≥ 選択枠右端
            const maxX = sel.x;
            const minX = sel.x + sel.width - imgSize.width;
            if (pos.x > maxX) pos.x = maxX;
            if (pos.x < minX) pos.x = minX;
        } else {
            // contain: 画像が選択枠内に収まる
            const minX = sel.x;
            const maxX = sel.x + sel.width - imgSize.width;
            if (pos.x < minX) pos.x = minX;
            if (pos.x > maxX) pos.x = maxX;
        }

        if (imgSize.height >= sel.height) {
            const maxY = sel.y;
            const minY = sel.y + sel.height - imgSize.height;
            if (pos.y > maxY) pos.y = maxY;
            if (pos.y < minY) pos.y = minY;
        } else {
            const minY = sel.y;
            const maxY = sel.y + sel.height - imgSize.height;
            if (pos.y < minY) pos.y = minY;
            if (pos.y > maxY) pos.y = maxY;
        }
    }

    /**
     * 描画
     */
    draw() {
        if (!this.ctx || !this.imageLoaded) return;

        const ctx = this.ctx;
        const sel = this.selection;
        const pos = this.imagePosition;
        const imgSize = this.getImageSize();

        // キャンバスをクリア
        ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

        // 背景
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

        // 画像を描画
        ctx.drawImage(
            this.image,
            pos.x,
            pos.y,
            imgSize.width,
            imgSize.height
        );

        if (this.circularCrop) {
            // 円形クロップモード: 円の外側を暗くする
            ctx.save();
            ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
            ctx.beginPath();
            ctx.rect(0, 0, this.canvasWidth, this.canvasHeight);
            // 円をくり抜く
            const centerX = sel.x + sel.width / 2;
            const centerY = sel.y + sel.height / 2;
            const radius = sel.width / 2;
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, true);
            ctx.fill('evenodd');
            ctx.restore();

            // 円形の枠線
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.stroke();
        } else {
            // 矩形クロップモード: 選択枠の外側を暗くする
            ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';

            // 上
            ctx.fillRect(0, 0, this.canvasWidth, sel.y);
            // 下
            ctx.fillRect(0, sel.y + sel.height, this.canvasWidth, this.canvasHeight - sel.y - sel.height);
            // 左
            ctx.fillRect(0, sel.y, sel.x, sel.height);
            // 右
            ctx.fillRect(sel.x + sel.width, sel.y, this.canvasWidth - sel.x - sel.width, sel.height);

            // 選択枠の枠線
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
            ctx.lineWidth = 2;
            ctx.strokeRect(sel.x, sel.y, sel.width, sel.height);
        }

        // グリッドライン（3x3）※円形クロップ時は非表示
        if (!this.circularCrop) {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.lineWidth = 1;

            // 縦線
            const thirdWidth = sel.width / 3;
            ctx.beginPath();
            ctx.moveTo(sel.x + thirdWidth, sel.y);
            ctx.lineTo(sel.x + thirdWidth, sel.y + sel.height);
            ctx.moveTo(sel.x + thirdWidth * 2, sel.y);
            ctx.lineTo(sel.x + thirdWidth * 2, sel.y + sel.height);
            ctx.stroke();

            // 横線
            const thirdHeight = sel.height / 3;
            ctx.beginPath();
            ctx.moveTo(sel.x, sel.y + thirdHeight);
            ctx.lineTo(sel.x + sel.width, sel.y + thirdHeight);
            ctx.moveTo(sel.x, sel.y + thirdHeight * 2);
            ctx.lineTo(sel.x + sel.width, sel.y + thirdHeight * 2);
            ctx.stroke();
        }
    }

    /**
     * イベントリスナーを追加
     */
    addEventListeners() {
        this.canvas.addEventListener('mousedown', this.boundMouseDown);
        document.addEventListener('mousemove', this.boundMouseMove);
        document.addEventListener('mouseup', this.boundMouseUp);

        this.canvas.addEventListener('touchstart', this.boundTouchStart, { passive: false });
        document.addEventListener('touchmove', this.boundTouchMove, { passive: false });
        document.addEventListener('touchend', this.boundTouchEnd);
    }

    /**
     * イベントリスナーを削除
     */
    removeEventListeners() {
        if (this.canvas) {
            this.canvas.removeEventListener('mousedown', this.boundMouseDown);
            this.canvas.removeEventListener('touchstart', this.boundTouchStart);
        }
        document.removeEventListener('mousemove', this.boundMouseMove);
        document.removeEventListener('mouseup', this.boundMouseUp);
        document.removeEventListener('touchmove', this.boundTouchMove);
        document.removeEventListener('touchend', this.boundTouchEnd);
    }

    /**
     * マウス座標を取得
     */
    getMousePos(e) {
        const rect = this.canvas.getBoundingClientRect();
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
    }

    /**
     * タッチ座標を取得
     */
    getTouchPos(e) {
        const rect = this.canvas.getBoundingClientRect();
        const touch = e.touches[0];
        return {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top
        };
    }

    handleMouseDown(e) {
        this.isDragging = true;
        this.dragStart = this.getMousePos(e);
        this.imagePositionStart = { ...this.imagePosition };
        this.canvas.style.cursor = 'grabbing';
        e.preventDefault();
    }

    handleMouseMove(e) {
        if (!this.isDragging) return;

        const pos = this.getMousePos(e);
        const dx = pos.x - this.dragStart.x;
        const dy = pos.y - this.dragStart.y;

        this.imagePosition.x = this.imagePositionStart.x + dx;
        this.imagePosition.y = this.imagePositionStart.y + dy;

        this.constrainImagePosition();
        this.draw();
    }

    handleMouseUp() {
        this.isDragging = false;
        if (this.canvas) {
            this.canvas.style.cursor = 'grab';
        }
    }

    /**
     * 2点間の距離を計算
     */
    getTouchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * 2点間の中心を取得
     */
    getTouchCenter(touches) {
        const rect = this.canvas.getBoundingClientRect();
        return {
            x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
            y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top
        };
    }

    handleTouchStart(e) {
        if (e.touches.length === 1) {
            // シングルタッチ: ドラッグ
            this.isDragging = true;
            this.isPinching = false;
            this.dragStart = this.getTouchPos(e);
            this.imagePositionStart = { ...this.imagePosition };
            e.preventDefault();
        } else if (e.touches.length === 2) {
            // 2本指: ピンチズーム開始
            this.isDragging = false;
            this.isPinching = true;
            this.pinchStartDistance = this.getTouchDistance(e.touches);
            this.pinchStartZoom = this.currentZoom;
            this.pinchCenter = this.getTouchCenter(e.touches);
            this.imagePositionStart = { ...this.imagePosition };
            e.preventDefault();
        }
    }

    handleTouchMove(e) {
        if (e.touches.length === 2 && this.isPinching) {
            // ピンチズーム中
            const currentDistance = this.getTouchDistance(e.touches);
            const scale = currentDistance / this.pinchStartDistance;
            const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.pinchStartZoom * scale));

            // ピンチ中心を基準にズーム
            const center = this.getTouchCenter(e.touches);
            const oldScale = this.imageInfo.baseScale * this.currentZoom;
            const imgCenterX = (this.pinchCenter.x - this.imagePositionStart.x) / (this.imageInfo.baseScale * this.pinchStartZoom);
            const imgCenterY = (this.pinchCenter.y - this.imagePositionStart.y) / (this.imageInfo.baseScale * this.pinchStartZoom);

            this.currentZoom = newZoom;
            const newScale = this.imageInfo.baseScale * this.currentZoom;
            this.imagePosition.x = center.x - imgCenterX * newScale;
            this.imagePosition.y = center.y - imgCenterY * newScale;

            this.constrainImagePosition();
            this.draw();
            this.updateSlider();
            e.preventDefault();
        } else if (this.isDragging && e.touches.length === 1) {
            // シングルタッチ: ドラッグ
            const pos = this.getTouchPos(e);
            const dx = pos.x - this.dragStart.x;
            const dy = pos.y - this.dragStart.y;

            this.imagePosition.x = this.imagePositionStart.x + dx;
            this.imagePosition.y = this.imagePositionStart.y + dy;

            this.constrainImagePosition();
            this.draw();
            e.preventDefault();
        }
    }

    handleTouchEnd(e) {
        if (e.touches.length === 0) {
            this.isDragging = false;
            this.isPinching = false;
        } else if (e.touches.length === 1 && this.isPinching) {
            // ピンチ終了後、1本指に戻ったらドラッグモードに
            this.isPinching = false;
            this.isDragging = true;
            this.dragStart = this.getTouchPos({ touches: e.touches });
            this.imagePositionStart = { ...this.imagePosition };
        }
    }

    /**
     * ズームイン
     */
    zoomIn() {
        const newZoom = Math.min(this.maxZoom, this.currentZoom + 0.1);
        this.applyZoom(newZoom);
    }

    /**
     * ズームアウト
     */
    zoomOut() {
        const newZoom = Math.max(this.minZoom, this.currentZoom - 0.1);
        this.applyZoom(newZoom);
    }

    /**
     * スライダーでズームを設定（0-100 → 1.0-3.0）
     */
    setZoom(value) {
        const sliderValue = parseFloat(value);
        // 0 = 1.0x（最小）, 100 = 3.0x（最大）
        const newZoom = this.minZoom + (sliderValue / 100) * (this.maxZoom - this.minZoom);
        this.applyZoom(newZoom);
    }

    /**
     * ズームを適用
     */
    applyZoom(newZoom) {
        const oldZoom = this.currentZoom;
        this.currentZoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));

        // ズームの中心を選択枠の中心にする
        const sel = this.selection;
        const centerX = sel.x + sel.width / 2;
        const centerY = sel.y + sel.height / 2;

        // ズーム前の画像上での中心位置
        const oldScale = this.imageInfo.baseScale * oldZoom;
        const imgCenterX = (centerX - this.imagePosition.x) / oldScale;
        const imgCenterY = (centerY - this.imagePosition.y) / oldScale;

        // ズーム後の位置を計算
        const newScale = this.imageInfo.baseScale * this.currentZoom;
        this.imagePosition.x = centerX - imgCenterX * newScale;
        this.imagePosition.y = centerY - imgCenterY * newScale;

        this.constrainImagePosition();
        this.draw();
        this.updateSlider();
    }

    /**
     * スライダーの値を更新
     */
    updateSlider() {
        if (!this.zoomSlider) return;
        const value = ((this.currentZoom - this.minZoom) / (this.maxZoom - this.minZoom)) * 100;
        this.zoomSlider.value = Math.round(value);
    }

    /**
     * モーダルを閉じる
     */
    close() {
        this.removeEventListeners();

        if (this.modal) {
            this.modal.style.display = 'none';
        }
        document.body.style.overflow = '';

        if (this.container) {
            this.container.innerHTML = '';
        }

        this.canvas = null;
        this.ctx = null;
        // ImageBitmap のリソースを明示解放（GC待ちせず即時開放）
        if (this.image && typeof this.image.close === 'function') {
            this.image.close();
        }
        this.image = null;
        this.imageLoaded = false;
        this.currentFile = null;
        this.callback = null;
        this.currentZoom = 1.0;
        this.circularCrop = false;
        this.isDragging = false;

        if (this.zoomSlider) {
            this.zoomSlider.value = 0;
        }
    }

    /**
     * クロップして保存
     */
    save() {
        if (!this.imageLoaded) {
            console.error('Image not loaded');
            return;
        }

        const callback = this.callback;
        const currentFile = this.currentFile;

        if (!callback) {
            console.error('No callback defined');
            this.close();
            return;
        }

        // 出力サイズ
        let outputWidth, outputHeight;
        if (this.circularCrop) {
            // 円形クロップ: 正方形で出力（実際の円形マスクはCSSで適用）
            outputWidth = 800;
            outputHeight = 800;
        } else {
            outputWidth = 1280;
            outputHeight = Math.round(outputWidth / this.aspectRatio);
        }

        // 選択枠の各辺の画像内相対座標（0〜1 を超える場合は画像外＝余白）
        const sel = this.selection;
        const pos = this.imagePosition;
        const imgSize = this.getImageSize();

        const relLeft = (sel.x - pos.x) / imgSize.width;
        const relTop = (sel.y - pos.y) / imgSize.height;
        const relRight = (sel.x + sel.width - pos.x) / imgSize.width;
        const relBottom = (sel.y + sel.height - pos.y) / imgSize.height;

        // ソース範囲を画像内 (0〜1) にクランプ
        const srcRelLeft = Math.max(0, relLeft);
        const srcRelTop = Math.max(0, relTop);
        const srcRelRight = Math.min(1, relRight);
        const srcRelBottom = Math.min(1, relBottom);

        const srcX = srcRelLeft * this.imageInfo.naturalWidth;
        const srcY = srcRelTop * this.imageInfo.naturalHeight;
        const srcWidth = (srcRelRight - srcRelLeft) * this.imageInfo.naturalWidth;
        const srcHeight = (srcRelBottom - srcRelTop) * this.imageInfo.naturalHeight;

        // 出力Canvas内での描画位置（クランプ前の選択枠範囲を outputWidth/Height にマップ）
        const relRangeX = relRight - relLeft;
        const relRangeY = relBottom - relTop;
        const dstX = ((srcRelLeft - relLeft) / relRangeX) * outputWidth;
        const dstY = ((srcRelTop - relTop) / relRangeY) * outputHeight;
        const dstWidth = ((srcRelRight - srcRelLeft) / relRangeX) * outputWidth;
        const dstHeight = ((srcRelBottom - srcRelTop) / relRangeY) * outputHeight;

        // クロップ用のCanvasを作成
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = outputWidth;
        cropCanvas.height = outputHeight;
        const cropCtx = cropCanvas.getContext('2d');

        // 余白を白で塗りつぶし（JPEGは透過不可のため）
        cropCtx.fillStyle = '#ffffff';
        cropCtx.fillRect(0, 0, outputWidth, outputHeight);

        // 画像の選択部分を描画（ソース範囲が有効な場合のみ）
        if (srcWidth > 0 && srcHeight > 0) {
            cropCtx.drawImage(
                this.image,
                srcX, srcY, srcWidth, srcHeight,
                dstX, dstY, dstWidth, dstHeight
            );
        }

        // Blobに変換
        cropCanvas.toBlob((blob) => {
            if (blob) {
                const fileName = currentFile ? currentFile.name : 'cropped-image.jpg';
                const croppedFile = new File([blob], fileName, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                });

                this.close();
                callback(croppedFile);
            } else {
                console.error('Failed to create blob');
                this.close();
            }
        }, 'image/jpeg', 0.92);
    }
}

// グローバルインスタンスを作成
window.imageCropper = new ImageCropper();

// DOMContentLoadedで初期化
document.addEventListener('DOMContentLoaded', function() {
    window.imageCropper.init();
});

// Livewire navigateイベント対応
document.addEventListener('livewire:navigated', function() {
    window.imageCropper.init();
});
